import React, { useCallback, useEffect, useState } from "react"
import { useController, useFormContext } from "react-hook-form"
import {
  Autocomplete as MuiAutocomplete,
  FilterOptionsState,
  TextField,
  CircularProgress,
  AutocompleteProps,
  Tooltip,
  IconButton,
} from "@mui/material"
import cs from "classnames"
import { normalizeLabel, preventSubmitOnEnter, trackFormEvent } from "../utils"
import { FormField, Option } from "../types"
import { WithParticleOverlay } from "../WithParticleOverlay"

import styles from "../Forms.module.scss"
import { Chip } from "./Chip"

export interface Props extends FormField, Pick<AutocompleteProps<Option, boolean, boolean, boolean>, "sx"> {
  defaultValue?: Option

  /**
   * If provided, will populate additional "Add new ..." option that calls this callback if
   * user input filtering did not give any results. Returns a promise with created resource ID.
   */
  onCreateNewClick?: (data: string) => void

  /**
   * If `true` - render "create new" option when:
   * - input is not empty
   * - there are no available options with a case-insensitively matching label
   */
  forceCreateNewOption?: boolean

  createNewTextGenerator?: (data: string) => string

  options?: Option[]

  /**
   * If set to true, simply renderes all options
   */
  disableFiltering?: boolean

  disableClearable?: boolean

  multiple?: boolean

  groupBy?: (option: Option) => string

  loadingOptions?: boolean

  isCreatingOption?: boolean

  onInputChange?: (input: string) => void

  disabledOptions?: string[]

  hideChips?: boolean

  renderChipsUnder?: boolean

  popperClassName?: string

  hideSelectedOptions?: boolean

  noOptionsText?: string

  clearInputOnSelect?: boolean

  placeholder?: string

  sortOptions?: boolean

  blurOnSelect?: boolean

  startAdornment?: React.ReactNode

  filterOptions?: (options: Option[], { inputValue }: FilterOptionsState<Option>) => Option[]

  /**
   * If true, the input will not be parsed into separate options separated by commas
   */
  rawNewValue?: boolean

  /**
   * If true, selecting an option will only call the `onOptionSelected()` callback.
   */
  preventWriteToForm?: boolean

  onOptionSelected?: (selection: Option | Option[] | null) => void

  /**
   * If `true` - each selected option will be either "positive" or "negative", which can be toggled by a button withing the chip.
   * Positive vs negative will be stored in option's `data` field.
   */
  enableNegationOptions?: boolean
}

const ADD_NEW_OPTION_VALUE = "ADD_NEW_OPTION"

const getAddNewOption = (
  inputValue: string,
  availableOptions: Option[],
  selectedOptions: Option[],
  labelGenerator?: (data: string) => string,
  rawNewValue?: boolean,
  forceCreateNewOption?: boolean
) => {
  if (forceCreateNewOption && !availableOptions.find((o) => o.label.toLowerCase() === inputValue.toLowerCase())) {
    return {
      data: inputValue,
      label: labelGenerator?.(inputValue) || `Add "${inputValue}"`,
      value: ADD_NEW_OPTION_VALUE,
    }
  }

  if (rawNewValue) {
    return {
      data: inputValue,
      label: labelGenerator?.(inputValue) || `Add "${inputValue}"`,
      value: ADD_NEW_OPTION_VALUE,
    }
  }

  /**
   * Clean up the input value to remove repeating commas, adjacent commas and quotes
   */
  const cleanInput = inputValue
    .trim()
    .replace(/,+/g, ", ")
    .replace(/"/g, "")
    .replace(/^,+|,+$/g, "")

  const words = Array.from(new Set(cleanInput.split(/\s*,\s*/)))

  /**
   * Words that are not met within available or selected options should be suggested as "add new" option
   */
  const validWords = Array.from(
    words.filter(
      (v) =>
        !!v &&
        !selectedOptions?.some((o) => o.label.toLowerCase() === v.toLowerCase()) &&
        !availableOptions?.some((o) => o.label.toLowerCase() === v.toLowerCase())
    )
  )

  if (validWords.length === 0) return null

  const combinedValues = validWords.join(",")

  return {
    data: combinedValues,
    label: labelGenerator?.(combinedValues) || `Add ${validWords.map((v) => `"${v.trim()}"`).join(", ")}`,
    value: ADD_NEW_OPTION_VALUE,
  }
}

const Autocomplete = React.forwardRef<any, Props>(function Autocomplete(
  {
    defaultValue,
    disableFiltering,
    label,
    name,
    className,
    onCreateNewClick,
    createNewTextGenerator,
    options: options_,
    rules,
    multiple,
    loadingOptions,
    isCreatingOption,
    onInputChange,
    disabledOptions,
    disabled,
    hideChips,
    popperClassName,
    hideSelectedOptions,
    noOptionsText,
    renderChipsUnder,
    groupBy,
    clearInputOnSelect,
    autoComplete,
    loading,
    placeholder,
    blurOnSelect,
    startAdornment,
    forceCreateNewOption,
    onOptionSelected,
    sortOptions = true,
    disableClearable = true,
    preventWriteToForm,
    tracking,
    rawNewValue,
    filterOptions,
    enableNegationOptions,
    ...props
  },
  ref
) {
  const { control, formState } = useFormContext()
  const controller = useController({ name, control, rules })
  const [inputValue, setInputValue] = useState("")
  const [isOpen, setIsOpen] = useState(false)

  const _filterOptions = React.useCallback(
    (options: Option[], state: FilterOptionsState<Option>) => {
      let normalizedInputValue = inputValue.trim().toLowerCase()

      if (!normalizedInputValue) return options

      const selectedOptions = multiple
        ? (controller.field.value as Option[])
        : [controller.field.value as Option].filter(Boolean)
      const filteredOptions = filterOptions
        ? filterOptions(options, state)
        : options.filter((option) => normalizeLabel(option).includes(normalizedInputValue))

      const addNewOption = onCreateNewClick
        ? getAddNewOption(
            inputValue,
            filteredOptions,
            selectedOptions,
            createNewTextGenerator,
            rawNewValue,
            forceCreateNewOption
          )
        : undefined

      return filteredOptions.concat(addNewOption ? [addNewOption] : [])
    },
    [
      inputValue,
      multiple,
      controller.field.value,
      filterOptions,
      onCreateNewClick,
      createNewTextGenerator,
      rawNewValue,
      forceCreateNewOption,
    ]
  )

  /**
   * If selected option in creatable autocomplete is "ADD_NEW_OPTION", call the handler.
   */
  const handleSelection = React.useCallback(
    async (e: React.SyntheticEvent<Element, Event>, option: Option | Option[] | null) => {
      if (preventWriteToForm) {
        onOptionSelected?.(option)
        return
      }

      if (clearInputOnSelect) setInputValue("")

      if (typeof option === "string") return

      // Handle multiselect
      if (Array.isArray(option)) {
        const creationData = option.find((o) => o.value === ADD_NEW_OPTION_VALUE)?.data

        if (creationData) {
          onCreateNewClick?.(creationData)
        } else {
          onOptionSelected?.(option)
          controller.field.onChange(option)

          if (tracking?.shouldTrackValueChange && option?.length) {
            await trackFormEvent("Form Value Provided", tracking, name, label)
          }
        }

        if (
          option.find((o) => o.value === "SELECT_ALL") ||
          options_?.filter((o) => o.value !== "SELECT_ALL").length === option.length
        ) {
          setIsOpen(false)
        }

        return
      }

      if (option?.value === ADD_NEW_OPTION_VALUE) {
        onCreateNewClick?.(option.data)
      } else {
        onOptionSelected?.(option)
        controller.field.onChange(option)

        if (tracking?.shouldTrackValueChange && option) {
          await trackFormEvent("Form Value Provided", tracking, name, label)
        }
      }
    },
    [
      clearInputOnSelect,
      preventWriteToForm,
      onOptionSelected,
      options_,
      onCreateNewClick,
      controller.field,
      tracking,
      name,
      label,
    ]
  )

  const handleInputChange = useCallback((_: React.SyntheticEvent<Element, Event>, input: string, reason: any) => {
    if (_?.type === "blur") {
      setInputValue("")
    } else if (reason !== "reset") {
      setInputValue(input)
    }
  }, [])

  const handleOptionDeleted = useCallback(
    (option: Option) => async () => {
      const newValue = controller.field.value.filter((o: Option) => o.value !== option.value)
      controller.field.onChange(newValue)
      if (tracking?.shouldTrackValueChange && newValue?.length) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [controller.field, label, name, tracking]
  )

  const handleNegationChange = useCallback(
    (option: Option, negative: boolean) => {
      const newValue = controller.field.value.map((o: Option) =>
        o.value === option.value ? { ...o, data: { ...o.data, negative } } : o
      )
      controller.field.onChange(newValue)
    },
    [controller.field]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    preventSubmitOnEnter(e)

    if (e.key === "Enter") {
      onCreateNewClick?.(inputValue.trim())
    }
  }

  useEffect(() => {
    onInputChange?.(inputValue)
  }, [inputValue, onInputChange])

  const error = formState.errors[name]
  const errorProps = error ? { error: true, label: error.message?.toString() } : {}

  const selectedOptions: Option[] = controller.field.value || []

  return (
    <WithParticleOverlay
      id={`${loading}${name}`}
      classes={{ root: className, childrenWrapper: styles.autocompleteRoot }}
      loading={loading}
    >
      <MuiAutocomplete
        {...props}
        ref={ref}
        classes={{ popper: popperClassName }}
        key={defaultValue?.value}
        disableClearable={disableClearable}
        filterOptions={loadingOptions ? undefined : _filterOptions}
        loading={loadingOptions}
        selectOnFocus
        noOptionsText={noOptionsText}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        open={isOpen}
        clearOnBlur
        blurOnSelect={blurOnSelect}
        disabled={disabled || isCreatingOption}
        getOptionDisabled={(o) => disabledOptions?.includes(o.value) || false}
        multiple={multiple}
        filterSelectedOptions={hideSelectedOptions}
        getOptionLabel={(o) => o.label}
        renderOption={(props, option) => {
          const li = (
            <li
              {...props}
              key={option.value}
              className={cs(props.className, option.className)}
              style={option.style}
              data-testid={`option-${option.value}`}
            >
              {option.label}
            </li>
          )

          if (option.tooltip) {
            return (
              <Tooltip
                title={option.tooltip}
                key={option.value}
                id={option.value}
                classes={{ popper: styles.tooltipPopper, tooltip: styles.tooltipTooltip }}
              >
                {li}
              </Tooltip>
            )
          }

          return li
        }}
        handleHomeEndKeys
        loadingText="Loading..."
        isOptionEqualToValue={(o, v) => normalizeLabel(o) == normalizeLabel(v)}
        renderInput={(params) => (
          <TextField
            label={label}
            {...params}
            {...errorProps}
            inputProps={{
              ...params.inputProps,
              ...(disableFiltering
                ? {
                    onChange: () => void 0,
                    className: cs(params.inputProps.className, styles.autocompleteInput),
                  }
                : {}),
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: startAdornment || params.InputProps.startAdornment,
              endAdornment: isCreatingOption ? <CircularProgress size={15} /> : params.InputProps.endAdornment,
            }}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={placeholder}
          />
        )}
        onInputChange={handleInputChange}
        inputValue={multiple ? inputValue : undefined}
        onChange={handleSelection}
        options={
          loadingOptions
            ? []
            : (sortOptions ? [...(options_ || [])]?.sort((a, b) => a.label.localeCompare(b.label)) : options_) || []
        }
        defaultValue={defaultValue}
        groupBy={groupBy}
        renderTags={
          renderChipsUnder || hideChips
            ? () => null
            : (options, getTagProps) => (
                <>
                  {options.map((o, index) => (
                    <Chip
                      option={o}
                      key={o.value}
                      onDeleteClick={handleOptionDeleted(o)}
                      enableNegationOptions={enableNegationOptions}
                      className={cs(getTagProps({ index }).className)}
                      onNegationChange={handleNegationChange}
                    />
                  ))}
                </>
              )
        }
        value={multiple ? controller.field.value || [] : controller.field.value || null}
      />

      {renderChipsUnder && selectedOptions.length > 0 && (
        <div className={styles.optionChips}>
          {selectedOptions.map((option) => (
            <Chip
              key={option.value}
              option={option}
              onDeleteClick={handleOptionDeleted(option)}
              enableNegationOptions={enableNegationOptions}
              onNegationChange={handleNegationChange}
            />
          ))}
        </div>
      )}
    </WithParticleOverlay>
  )
})

export default Autocomplete
